// /home/project/src/components/Layout/Layout.tsx
import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Header from './Header';
import SidebarNavigation from '../SidebarNavigation';
import { useAuth } from '../../contexts/AuthContext';
import { ENABLE_E2E_ROUTES } from '../../config/env';
import { useMvpRouteAccess } from '../../hooks/useMvpRouteAccess';
import { useNavigationShellState } from '../../hooks/useNavigationShellState';
import ResponsiveNavigationSidebar from './ResponsiveNavigationSidebar';
import { RouteContentBoundary } from '../router/RouteContentBoundary';
import {
  isAuthorityProjectionFresh,
  useAuthorityProjectionState,
} from '../../lib/auth/authorityProjectionStore';
import { authorityProjectionMatchesNavigationPath } from '../../services/AuthorityProjectionService';

const Layout: React.FC = () => {
  const { user, isDemoMode } = useAuth();
  const location = useLocation();
  const authorityProjection = useAuthorityProjectionState();
  const connectedProjectionOwnsRoute = isDemoMode === false && (
    /^\/workspaces\/(?!select(?:\/|$))[^/]+(?:\/|$)/.test(location.pathname)
    || /^\/companies\/(?!select(?:\/|$))[^/]+(?:\/|$)/.test(location.pathname)
    || /^\/projects\/[^/]+\/?$/.test(location.pathname)
    || /^\/invitations\/?$/.test(location.pathname)
    || /^\/settings\/invitations\/?$/.test(location.pathname)
  );
  const connectedNavigationConfirmed = isDemoMode !== false || (
    authorityProjection.status === 'confirmed'
    && authorityProjection.projection.actor.userId === user?.id
    && isAuthorityProjectionFresh(
      authorityProjection.projection,
      authorityProjection.validatedAt,
    )
    && authorityProjectionMatchesNavigationPath(
      authorityProjection.request,
      authorityProjection.projection,
      location.pathname,
    )
  );

  const navigation = useNavigationShellState();

  // Capital surface personas must use the dedicated app shell at /investor
  // Conditional return is placed AFTER all hooks to satisfy React Rules of Hooks
  const { decision: routeAccess, loading: routeAccessLoading } = useMvpRouteAccess(
    user,
    location.pathname,
  );

  if (!connectedProjectionOwnsRoute && routeAccessLoading) {
    return <div className="min-h-screen bg-app" aria-busy="true" />;
  }

  // Use the same authority for direct URLs as the sidebar. Denied routes never
  // mount their page or start its data hooks.
  if (!connectedProjectionOwnsRoute && !routeAccess.registered && location.pathname !== '/404') {
    return <Navigate to="/404" replace />;
  }
  if (!connectedProjectionOwnsRoute && !routeAccess.allowed && location.pathname !== '/unauthorized') {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <div className="ax1-product-shell h-full min-h-0 flex flex-col overflow-hidden bg-app transition-colors duration-300">
      {/* Header — premium glass chrome with scroll detection */}
      <Header
        onToggleSidebar={navigation.toggleNavigation}
        isSidebarOpen={navigation.isNavigationVisible}
        isSidebarCollapsed={navigation.isSidebarCollapsed}
        isMobileNavigation={navigation.isMobile}
        className="flex-shrink-0"
      />

      {/* Dev banner when E2E routes are enabled */}
      {ENABLE_E2E_ROUTES && (
        <div role="note" aria-label="E2E routes enabled"
          className="px-4 py-1 text-xs text-status-warning-foreground bg-status-warning-soft border-b border-status-warning/30">
          E2E test routes are enabled (VITE_ENABLE_E2E_ROUTES=true). Disable before production.
        </div>
      )}

      {/* App body */}
      <div className="flex flex-1 min-h-0 overflow-hidden relative">
        {connectedNavigationConfirmed && (
          <ResponsiveNavigationSidebar state={navigation} label="Primary navigation">
            <SidebarNavigation
              isCollapsed={navigation.isSidebarCollapsed}
            />
          </ResponsiveNavigationSidebar>
        )}

        {/* Main content region - Shell is neutral, each page owns its layout */}
        <main
          id="main-content"
          role="main"
          aria-label="Main content"
          className="flex-1 min-w-0 overflow-hidden flex flex-col"
        >
          <RouteContentBoundary key={`${user?.id}:${user?.account_role}:${user?.workspace_id}:${location.pathname.match(/^\/workspaces\/([^/]+)/)?.[1] ?? user?.workspace_id}`}>
            <Outlet />
          </RouteContentBoundary>
        </main>
      </div>
    </div>
  );
};

export default Layout;
